import React from "react";
import { getCardImage } from "../resultVisuals/cardAssets";
import { formatBook } from "../../../utils/formatOdds";
import CardImg from "../../../components/CardImg";

/**
 * Baccarat / Baccarat 2 / Premium Baccarat bet table in the reference admin markup:
 *   .baccarat-bets-odds.pt-3 > .baccarat-odds (side bets, mainData[5..8], +[9] for baccarat2)
 *                            + .baccarat-bets (player-pair, player, tie, banker, banker-pair)
 * mainData = feed `sub` ordered by sid: 0 Player, 1 Banker, 2 Tie, 3 Player Pair, 4 Banker Pair, 5.. side bets.
 * Player shows cards [4 (l-rotate, if drawn), 2, 0]; Banker shows [1, 3, 5 (r-rotate, if drawn)].
 * Styled by the ported reference rules (.baccarat …) in styles/index.css.
 */

// Reference Card component: <span><img src=".../cards/{card}.png"></span>
function Card({ card, className }) {
  return (
    <span className={className}>
      <CardImg src={getCardImage(card)} alt="" />
    </span>
  );
}

export function BetTableBaccarat({ data = [], cards = [], gameType = "baccarat", onBetClick, exposures = {}, myBets = [] }) {
  const mainData = [...data].sort((a, b) => Number(a?.sid) - Number(b?.sid));

  const getExposure = (betName) => {
    if (!betName) return 0;
    return exposures[betName] ?? exposures[betName.toLowerCase()] ?? 0;
  };

  // Reference: suspended whenever gstatus != "OPEN"
  const suspendedCls = (item) => (!item || item.gstatus !== "OPEN" ? " suspended" : "");
  const isSuspended = (item) =>
    !item || item.gstatus === "SUSPENDED" || item.gstatus !== "OPEN" || item.b === 0 || item.b === undefined;
  const rate = (item) => `${parseFloat(item?.b) || 0}:1`;

  const handleClick = (item) => {
    if (!item || isSuspended(item)) return;
    onBetClick?.(item.nat, item.b, item);
  };

  // Book value (exposure) — shown once a bet is placed on the market, "0" otherwise
  const book = (item) => {
    const nat = item?.nat || "";
    const placedBet = myBets.find((b) => {
      const sel = (b.matchedBet || b.selection || b.player_name || "").toLowerCase().trim();
      return sel === nat.toLowerCase().trim();
    });
    if (!placedBet) return { cls: "book-red", text: "0" };
    const exposure = getExposure(placedBet.matchedBet || placedBet.selection || placedBet.player_name || nat);
    return { cls: exposure > 0 ? "book-green" : "book-red", text: formatBook(exposure) };
  };

  if (!mainData.length) return null;

  const sideIdx = gameType === "baccarat2" ? [5, 6, 7, 8, 9] : [5, 6, 7, 8];
  const c = (i) => String(cards[i] ?? "1").trim() || "1";

  const pairCell = (cls, item) => {
    const bk = book(item);
    return (
      <div className={cls}>
        <div className={`baccarat-bets-name${suspendedCls(item)}`} onClick={() => handleClick(item)}>
          <div>{item?.nat}</div> <div className="mb-0">{rate(item)}</div>
        </div>{" "}
        <div className={`baccarat-bets-val ${bk.cls}`}>{bk.text}</div>
      </div>
    );
  };

  const player = mainData[0];
  const banker = mainData[1];
  const playerBook = book(player);
  const bankerBook = book(banker);

  return (
    <div className="baccarat-bets-odds pt-3">
      <div className="baccarat-odds">
        {sideIdx.map((i, n) => {
          const item = mainData[i];
          if (!item) return null;
          const bk = book(item);
          return (
            <React.Fragment key={item.sid ?? i}>
              {n > 0 && " "}
              <div className="baccarat-odd-block">
                <div className={`baccarat-odd-name${suspendedCls(item)}`} onClick={() => handleClick(item)}>
                  {`${item.nat} ${rate(item)}`}
                </div>{" "}
                <div className={`baccarat-odd-val ${bk.cls}`}>{bk.text}</div>
              </div>
            </React.Fragment>
          );
        })}
      </div>{" "}
      <div className="baccarat-bets">
        {pairCell("player-pair", mainData[3])}{" "}
        <div className="player">
          <div className={`baccarat-bets-name${suspendedCls(player)}`} onClick={() => handleClick(player)}>
            <div>{`${player?.nat} ${rate(player)}`}</div>{" "}
            <div className="mb-0">
              {c(4) !== "1" && <Card card={c(4)} className="l-rotate" />} <Card card={c(2)} /> <Card card={c(0)} />
            </div>
          </div>{" "}
          <div className={`baccarat-bets-val ${playerBook.cls}`}>{playerBook.text}</div>
        </div>{" "}
        {pairCell("tie", mainData[2])}{" "}
        <div className="banker">
          <div className={`baccarat-bets-name${suspendedCls(banker)}`} onClick={() => handleClick(banker)}>
            <div>{`${banker?.nat} ${rate(banker)}`}</div>{" "}
            <div className="mb-0">
              <Card card={c(1)} /> <Card card={c(3)} /> {c(5) !== "1" && <Card card={c(5)} className="r-rotate" />}
            </div>
          </div>{" "}
          <div className={`baccarat-bets-val ${bankerBook.cls}`}>{bankerBook.text}</div>
        </div>{" "}
        {pairCell("banker-pair", mainData[4])}
      </div>
    </div>
  );
}

export default BetTableBaccarat;
